import { useState, useEffect, FormEvent } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useCategories } from '@/contexts/CategoriesContext';
import type { Transaction } from '@/types';

interface EditTransactionModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onUpdate: (transactionId: string, updates: {
    type: 'gain' | 'expense';
    name: string;
    amount: number;
    categoryId: string;
    date: Date;
    notes?: string;
  }) => Promise<void>;
}

export function EditTransactionModal({ 
  isOpen, 
  transaction,
  onClose, 
  onUpdate,
}: EditTransactionModalProps) {
  const { t } = useTranslation();
  const { gainCategories, expenseCategories } = useCategories();
  const [type, setType] = useState<'gain' | 'expense'>('expense');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = type === 'gain' ? gainCategories : expenseCategories;

  // Pre-fill form when transaction changes
  useEffect(() => {
    if (transaction) {
      setType(transaction.type);
      setName(transaction.name);
      setAmount(transaction.amount.toString());
      setCategoryId(transaction.categoryId);
      setNotes(transaction.notes || '');
      setError('');
    }
  }, [transaction]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!transaction) return;
    
    if (!name.trim()) {
      setError(t('errors.enterName'));
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError(t('errors.enterAmount'));
      return;
    }

    if (!categoryId) {
      setError(t('errors.selectCategory'));
      return;
    }

    setLoading(true);
    try {
      await onUpdate(transaction.id, {
        type,
        name: name.trim(),
        amount: parseFloat(amount),
        categoryId,
        date: transaction.date, // Keep original date
        notes: notes.trim() || undefined,
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating transaction:', error);
      setError(t('errors.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (newType: 'gain' | 'expense') => {
    setType(newType);
    setCategoryId(''); // Reset category when type changes
  };

  if (!isOpen || !transaction) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('common.edit')} {t('transaction.type')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Type Toggle */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('transaction.type')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleTypeChange('expense')}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                  type === 'expense'
                    ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                💳 {t('transaction.expense')}
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('gain')}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                  type === 'gain'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                💰 {t('transaction.income')}
              </button>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('transaction.name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:text-slate-100"
              placeholder={t('transaction.enterName')}
              disabled={loading}
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('transaction.amount')} *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:text-slate-100"
              placeholder={t('transaction.enterAmount')}
              disabled={loading}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('transaction.category')} *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:text-slate-100"
              disabled={loading}
            >
              <option value="">{t('transaction.selectCategory')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('transaction.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:text-slate-100 resize-none"
              rows={3}
              placeholder={t('transaction.addNotes')}
              disabled={loading}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? `${t('common.update')}...` : t('common.update')}
          </button>
        </form>
      </div>
    </div>
  );
}